import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
    EnvelopeIcon,
    PhoneIcon,
    MapPinIcon
} from '@heroicons/react/24/outline';
import { API } from '../config';
import logo from '../images/FINAL-DESIGN-LOGO.png';

const Footer = () => {
    const [settings, setSettings] = useState({
        tagline: 'From Farm to Table • Naturally Raised • Hygienically Processed',
        description: 'A trusted poultry brand delivering naturally raised, antibiotic-free poultry with uncompromising quality.',
        email: 'info@srdailynutrition.com',
        phone: '+91 92427 92427',
        location: 'Bengaluru',
        copyright: '© 2025 SR Daily Nutrition. All rights reserved.',
        facebook: '',
        instagram: '',
        linkedin: ''
    });

    const [links, setLinks] = useState([]);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axios.get(`${API}/footer`);
                setSettings(response.data);
            } catch (error) {
                console.error('Failed to fetch footer settings:', error);
            }
        };
        fetchSettings();
    }, []);

    return (
        <footer className="bg-[#FDFDFD] border-t border-gray-200 pt-24 pb-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-16 mb-20">
                    {/* Column 1: Brand Info */}
                    <div className="lg:col-span-4">
                        <Link to="/" className="inline-block mb-8">
                            <img src={logo} alt="SR Daily Nutrition" className="h-14 w-auto object-contain" />
                        </Link>
                        <div className="space-y-4">
                            <p className="text-gray-900 font-bold text-sm tracking-tight leading-snug">
                                {settings.tagline}
                            </p>
                            <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                                {settings.description}
                            </p>
                        </div>
                    </div>

                    {/* Column 2: Navigation */}
                    <div className="lg:col-span-2 lg:border-l lg:border-gray-100 lg:pl-12">
                        <h4 className="text-gray-900 font-bold text-xs uppercase tracking-widest mb-8">Navigation</h4>
                        <ul className="space-y-4">
                            <li>
                                <Link to="/" className="text-gray-500 hover:text-[#E31E24] transition-colors text-sm font-medium">
                                    Home
                                </Link>
                            </li>
                            <li>
                                <button
                                    onClick={() => {
                                        const element = document.getElementById('about-us');
                                        if (element) {
                                            element.scrollIntoView({ behavior: 'smooth' });
                                        } else {
                                            window.location.href = '/#about-us';
                                        }
                                    }}
                                    className="text-gray-500 hover:text-[#E31E24] transition-colors text-sm font-medium text-left"
                                >
                                    About Us
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => {
                                        if (window.location.pathname !== '/') {
                                            window.location.href = '/';
                                            setTimeout(() => {
                                                const element = document.querySelector('.py-12.bg-gray-50');
                                                if (element) element.scrollIntoView({ behavior: 'smooth' });
                                            }, 100);
                                        } else {
                                            const element = document.querySelector('.py-12.bg-gray-50');
                                            if (element) element.scrollIntoView({ behavior: 'smooth' });
                                        }
                                    }}
                                    className="text-gray-500 hover:text-[#E31E24] transition-colors text-sm font-medium text-left"
                                >
                                    Our Products
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => {
                                        if (window.location.pathname !== '/') {
                                            window.location.href = '/';
                                            setTimeout(() => {
                                                const element = document.querySelector('.py-24.bg-white');
                                                if (element) element.scrollIntoView({ behavior: 'smooth' });
                                            }, 100);
                                        } else {
                                            const element = document.querySelector('.py-24.bg-white');
                                            if (element) element.scrollIntoView({ behavior: 'smooth' });
                                        }
                                    }}
                                    className="text-gray-500 hover:text-[#E31E24] transition-colors text-sm font-medium text-left"
                                >
                                    Visit Our Store
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Column 3: Contact Details */}
                    <div className="lg:col-span-6 lg:border-l lg:border-gray-100 lg:pl-12">
                        <h4 className="text-gray-900 font-bold text-xs uppercase tracking-widest mb-8">Contact Us</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-gray-400">
                                    <EnvelopeIcon className="h-4 w-4" />
                                    <span className="text-[10px] uppercase font-black tracking-widest">Email Support</span>
                                </div>
                                <a href={`mailto:${settings.email}`} className="block text-gray-600 hover:text-[#E31E24] text-sm font-medium transition-colors">
                                    {settings.email}
                                </a>
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-gray-400">
                                    <PhoneIcon className="h-4 w-4" />
                                    <span className="text-[10px] uppercase font-black tracking-widest">Call Registry</span>
                                </div>
                                <a href={`tel:${settings.phone}`} className="block text-gray-600 hover:text-[#E31E24] text-sm font-medium transition-colors">
                                    {settings.phone}
                                </a>
                            </div>
                            <div className="md:col-span-2 space-y-2">
                                <div className="flex items-center gap-2 text-gray-400">
                                    <MapPinIcon className="h-4 w-4" />
                                    <span className="text-[10px] uppercase font-black tracking-widest">Store Location</span>
                                </div>
                                <p className="text-gray-600 text-sm font-medium leading-relaxed max-w-md">
                                    {settings.location}
                                </p>
                            </div>

                            {/* Move Social Links here after Store Location */}
                            <div className="md:col-span-2 space-y-4 pt-4 border-t border-gray-50">
                                <span className="text-[10px] uppercase font-black tracking-widest text-gray-400">Social Connect</span>
                                <div className="flex items-center gap-3">
                                    {settings.instagram && (
                                        <a href={settings.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-50 border border-gray-100 text-gray-400 hover:text-[#E31E24] hover:border-[#E31E24]/20 hover:bg-orange-50 transition-all duration-300 group">
                                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
                                        </a>
                                    )}
                                    {settings.facebook && (
                                        <a href={settings.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-50 border border-gray-100 text-gray-400 hover:text-[#E31E24] hover:border-[#E31E24]/20 hover:bg-orange-50 transition-all duration-300 group">
                                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" /></svg>
                                        </a>
                                    )}
                                    {settings.linkedin && (
                                        <a href={settings.linkedin} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-50 border border-gray-100 text-gray-400 hover:text-[#E31E24] hover:border-[#E31E24]/20 hover:bg-orange-50 transition-all duration-300 group">
                                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pt-12 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-6">
                    <p className="text-gray-400 text-[13px] font-medium">
                        {settings.copyright}
                    </p>
                    <div className="flex items-center gap-8 text-[11px] text-gray-400 font-bold uppercase tracking-widest">
                        <Link to="/privacy" className="hover:text-gray-900 transition-colors">Privacy</Link>
                        <Link to="/terms" className="hover:text-gray-900 transition-colors">Terms</Link>
                        <Link to="/shipping" className="hover:text-gray-900 transition-colors">Shipping</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
